import React from 'react';
import {
  Container, Typography, Grid, Card, CardContent,
  Box, List, ListItem, ListItemIcon, ListItemText, Paper, Button
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DnsIcon from '@mui/icons-material/Dns';
import MemoryIcon from '@mui/icons-material/Memory';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';

const services = [
  'Server hardware diagnostics',
  'RAID array rebuild and recovery',
  'PSU (power supply) replacement',
  'CPU and heatsink replacement',
  'RAM expansion and replacement',
  'NIC / HBA card installation',
  'Storage drive replacement (HDD / SSD / NVMe)',
  'Server rack installation and cabling',
  'BIOS / firmware updates',
  'Overheating and cooling system repair',
  'NAS hardware maintenance',
  'Workstation hardware upgrades',
];

const benefits = [
  {
    icon: DnsIcon,
    title: 'Enterprise Grade',
    description: '16+ years of hands-on server and datacenter experience',
  },
  {
    icon: SpeedIcon,
    title: 'Minimal Downtime',
    description: 'Fast diagnosis and repair to keep your systems running',
  },
  {
    icon: SecurityIcon,
    title: 'Data Security',
    description: 'Safe handling with full data protection protocols',
  },
  {
    icon: MemoryIcon,
    title: 'All Hardware',
    description: 'Dell, HP, Cisco, Supermicro, and custom-built servers',
  },
];

const serverTypes = [
  { title: 'Rack Servers', description: 'Dell PowerEdge, HP ProLiant, Cisco UCS' },
  { title: 'Tower Servers', description: 'SMB and workstation-class tower servers' },
  { title: 'NAS & Storage', description: 'Synology, QNAP, and enterprise NAS arrays' },
  { title: 'Workstations', description: 'High-performance workstation hardware repair' },
];

function ServerRepair() {
  return (
    <Box sx={{ py: 6, bgcolor: 'background.default' }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography variant="h2" component="h1" className="gradient-text" sx={{ mb: 3 }}>
            Server Hardware Repair
          </Typography>
          <Typography variant="h5" sx={{ color: 'text.secondary', mb: 4 }}>
            Enterprise server hardware diagnosis, repair, and upgrades — minimal downtime
          </Typography>
          <Button
            component={RouterLink}
            to="/booking"
            variant="contained"
            size="large"
            sx={{
              py: 2, px: 4, fontSize: '1.1rem',
              background: 'linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%)',
              color: '#2d3436', fontWeight: 700,
              '&:hover': {
                background: 'linear-gradient(135deg, var(--primary) 20%, var(--secondary) 80%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 20px rgba(255,107,107,0.3)',
              },
            }}
          >
            Book Server Repair
          </Button>
        </Box>

        {/* Benefits */}
        <Grid container spacing={4} sx={{ mb: 8 }}>
          {benefits.map((benefit, i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Card className="glass-effect card-hover" sx={{ height: '100%' }}>
                <CardContent sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Box sx={{ mb: 2, color: 'primary.main' }}>
                    {React.createElement(benefit.icon, { sx: { fontSize: 40 } })}
                  </Box>
                  <Typography variant="h6" gutterBottom>{benefit.title}</Typography>
                  <Typography variant="body2" color="text.secondary">{benefit.description}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Services */}
        <Paper elevation={3} sx={{ p: 4, mb: 8, background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(10px)' }}>
          <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>Server Repair Services</Typography>
          <Grid container spacing={2}>
            {services.map((service, i) => (
              <Grid item xs={12} sm={6} key={i}>
                <List>
                  <ListItem>
                    <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                    <ListItemText primary={service} />
                  </ListItem>
                </List>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* Server types */}
        <Paper elevation={3} sx={{ p: 4, background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(10px)' }}>
          <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>Server Types We Service</Typography>
          <Grid container spacing={3}>
            {serverTypes.map((type, i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Card className="glass-effect" sx={{ textAlign: 'center', height: '100%', p: 2 }}>
                  <Typography variant="h6" gutterBottom>{type.title}</Typography>
                  <Typography variant="body2" color="text.secondary">{type.description}</Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Container>
    </Box>
  );
}

export default ServerRepair;
